// Suscripciones en vivo a Firestore. Las apps solo leen; las escrituras van por Cloud Functions.

import { useEffect, useState } from "react";
import { doc, onSnapshot, type DocumentData, type Query } from "firebase/firestore";
import { db } from "./firebase";

/** Documento con su id y su ruta completa (útil en consultas de grupo para saber el conjunto). */
export type ConId<T> = T & { id: string; ruta: string };

/**
 * Documento en vivo. `undefined` mientras carga, `null` si no existe o no hay permiso.
 * El estado guarda la ruta junto con los datos: al cambiar de ruta (p. ej. cuando llega la sesión) el
 * primer render ya devuelve `undefined` y no el `null` de la ruta anterior, que haría redirigir antes de tiempo.
 */
export function useDocumento<T = DocumentData>(ruta: string | null): ConId<T> | null | undefined {
  const [estado, setEstado] = useState<{ ruta: string; datos: ConId<T> | null } | null>(null);
  useEffect(() => {
    if (!ruta) return;
    return onSnapshot(
      doc(db, ruta),
      (s) => setEstado({ ruta, datos: s.exists() ? ({ id: s.id, ruta: s.ref.path, ...(s.data() as T) } as ConId<T>) : null }),
      (e) => {
        console.warn("Lectura rechazada", ruta, e.code);
        setEstado({ ruta, datos: null });
      },
    );
  }, [ruta]);
  if (!ruta) return null;
  return estado?.ruta === ruta ? estado.datos : undefined;
}

const NINGUNO: never[] = [];

/**
 * Consulta en vivo. `clave` identifica la consulta: cuando cambia, se vuelve a suscribir y mientras tanto
 * devuelve `undefined` (nunca la lista de la clave anterior). Pasa `null` para no consultar todavía.
 */
export function useConsulta<T = DocumentData>(clave: string | null, crear: () => Query): ConId<T>[] | undefined {
  const [estado, setEstado] = useState<{ clave: string; lista: ConId<T>[] } | null>(null);
  useEffect(() => {
    if (!clave) return;
    return onSnapshot(
      crear(),
      (s) => setEstado({ clave, lista: s.docs.map((d) => ({ id: d.id, ruta: d.ref.path, ...(d.data() as T) }) as ConId<T>) }),
      (e) => {
        console.warn("Consulta rechazada", clave, e.code, e.message);
        setEstado({ clave, lista: [] });
      },
    );
    // `crear` cambia en cada render; la clave es la que manda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);
  if (!clave) return NINGUNO;
  return estado?.clave === clave ? estado.lista : undefined;
}

/** Fuerza un render cada `ms` (para "hace 5 min" y cuentas regresivas). */
export function useReloj(ms = 30_000): Date {
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return ahora;
}

export function fecha(m: { toDate(): Date } | null | undefined): Date | null {
  return m && typeof m.toDate === "function" ? m.toDate() : null;
}

/** Identificador aleatorio. crypto.randomUUID solo existe en https o localhost, no al probar por la IP de la red local. */
export const idAleatorio = (): string =>
  crypto.randomUUID?.() ?? Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
